// react
import { createContext, useContext, useMemo, PropsWithChildren } from 'react';

// store
import { useSelector } from 'store';
import { getAccount, getIsNewKindeAccount } from 'store/slices/iamSlice';
import {
  Tenant,
  Session,
  AccountRole,
  OrganizationTypeID,
  Feature,
  Permission,
  accountFeatureToTenantFeature,
  type ActiveFeatureVersions,
} from 'context';

// models
import { AccountFeature, AccountFeatureName } from '@karehero/models';

// hooks
import { useAuth, usePermissions } from 'hooks';
import { useStableFlags } from 'hooks/useStableFlags';

function resolveAccountFeatures(accountFeatures: AccountFeature[]): Feature[] {
  const values = new Map<AccountFeatureName, boolean>(
    accountFeatures.map((f) => [f.name, f.value]),
  );
  const resolved: Feature[] = [];
  for (const [apiName, tenantFeature] of Object.entries(
    accountFeatureToTenantFeature,
  ) as [AccountFeatureName, Feature][]) {
    if (values.get(apiName) === true) resolved.push(tenantFeature);
  }
  return resolved;
}

interface TenantCtxValue {
  tenant: Tenant;
}

const TenantCtx = createContext<TenantCtxValue | null>(null);

export function TenantProvider({ children }: PropsWithChildren) {
  // hooks
  const { isAuthenticated } = useAuth();
  const account = useSelector(getAccount);
  const isNewKindeAccount = useSelector(getIsNewKindeAccount);
  const isCreateReferralOrganisation = account?.isCreateReferralOrganisation;
  const { flags, ready: featuresReady } = useStableFlags();
  const { bookCall } = usePermissions();

  // memos
  const session: Session = useMemo(
    () => ({
      isNewKindeAccount: isNewKindeAccount === true,
      isAuthenticated: isAuthenticated,
      isVerified:
        account?.organization &&
        !(
          [
            OrganizationTypeID.UNVERIFIED,
            OrganizationTypeID.NONE,
          ] as OrganizationTypeID[]
        ).includes(account?.organization?.organizationTypeId),
      isOnboarded: account?.isOnboarded,
      isReferringPartner: account?.isCreateReferralOrganisation,
    }),
    [isAuthenticated, account, isNewKindeAccount, isCreateReferralOrganisation],
  );

  const tenant = useMemo(() => {
    let accountRole = AccountRole.NONE;
    switch (account?.accountRole.name) {
      case AccountRole.ADMIN:
        accountRole = AccountRole.ADMIN;
        break;
      case AccountRole.ORG_OWNER:
        accountRole = AccountRole.ORG_OWNER;
        break;
      case AccountRole.MEMBER:
        accountRole = AccountRole.MEMBER;
        break;
      case AccountRole.PARTNER_ADMIN:
        accountRole = AccountRole.PARTNER_ADMIN;
        break;
    }

    const organizationTypeId: OrganizationTypeID =
      account?.organization?.organizationTypeId ?? OrganizationTypeID.NONE;

    let features: Feature[] = [];
    let permissions: Permission[] = [];

    if (flags.careCircle) features.push(Feature.CARE_CIRCLE);
    if (flags.attendanceAllowance) features.push(Feature.ATTENDANCE_ALLOWANCE);
    if (flags.financialStatus) features.push(Feature.FINANCIAL_STATUS);
    if (flags.externalReferralForm)
      features.push(Feature.EXTERNAL_REFERRAL_FORM);
    if (flags.allowTokenlessRegistration)
      features.push(Feature.ALLOW_TOKENLESS_REGISTRATION);
    if (flags.ifaPortal) features.push(Feature.IFA_PORTAL);
    if (flags.memberShortlists) features.push(Feature.SHORTLISTS_V2);
    if (flags.backtesting) features.push(Feature.BACKTESTING);
    if (flags.dynamicChatSandbox) features.push(Feature.DYNAMIC_CHAT_SANDBOX);
    if (account?.features) {
      features.push(...resolveAccountFeatures(account.features));
    }
    if (session.isAuthenticated ? bookCall.isModify : true)
      permissions.push(Permission.BOOK_CALL);

    const activeFeatureVersions: ActiveFeatureVersions = {};

    for (const f of account?.features ?? []) {
      if (!f.value) continue;

      switch (f.name as string) {
        case 'benefits-calculator':
          activeFeatureVersions.benefitsCalculator = flags.benefitsCalculatorV2
            ? 'v2'
            : 'v1';
      }
    }

    return {
      accountRole: accountRole,
      organizationTypeId,
      session: session,
      features: features,
      featuresReady,
      permissions: permissions,
      activeFeatureVersions,
    };
  }, [account, flags, featuresReady, bookCall, session]);

  const value: TenantCtxValue = useMemo(() => ({ tenant }), [tenant]);

  return <TenantCtx.Provider value={value}>{children}</TenantCtx.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantCtx);
  if (!ctx) throw new Error('useTenant must be used within <TenantProvider>');
  return ctx;
}
